import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { Activity, Calendar, CheckCircle2, Clock, Filter, History, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AttendanceCard, ClockInButton } from '@/components/attendance';
import { useAttendance } from '@/hooks';
import { checkClockInTimeRestriction, formatMinutes, formatNaira } from '@/utils';
import { cn } from '@/utils/cn';

export function Attendance() {
  const {
    todayAttendance,
    attendanceRecords,
    summary,
    isLoading: attendanceLoading,
    canClockIn,
    canClockOut,
    fetchMyAttendance,
    fetchMySummary,
  } = useAttendance(false);
  const [timeRestriction, setTimeRestriction] = useState<{
    isAllowed: boolean;
    message?: string;
  } | null>(null);

  useEffect(() => {
    const restriction = checkClockInTimeRestriction(todayAttendance?.clockedIn ?? false);
    setTimeRestriction(restriction);
  }, [todayAttendance?.clockedIn]);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() => {
    const now = dayjs();
    return [now.startOf('month'), now.endOf('month')];
  });

  useEffect(() => {
    if (!dateRange) return;

    fetchMyAttendance(dateRange[0].format('YYYY-MM-DD'), dateRange[1].format('YYYY-MM-DD'));
    fetchMySummary(dateRange[0].year(), dateRange[0].month() + 1);
  }, [dateRange, fetchMyAttendance, fetchMySummary]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Attendance Logs
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-sm font-medium">
            Review your historical clock-in data and punctuality metrics.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 rounded-xl dark:bg-neolab-forest bg-gradient-to-l dark:from-accent-orange/30 to-transparent shadow-xl shadow-slate-200/50 dark:shadow-none relative overflow-hidden border border-slate-100 dark:border-none"
      >
        <div className="absolute top-0 right-0 w-1/3 h-full" />
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 text-slate-900 dark:text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-accent shadow-lg shadow-orange-500/20 flex items-center justify-center">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <div className="text-slate-900 dark:text-white">
              <h3 className="text-lg font-black tracking-tight mb-1 text-slate-900 dark:text-white">
                Today's Activity
              </h3>
              {todayAttendance?.clockedIn ? (
                <div className="flex flex-wrap gap-4 text-sm font-bold">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-white">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-500 dark:text-white">
                      In: {todayAttendance.attendance?.clockInTime}
                    </span>
                  </div>
                  {todayAttendance.attendance?.clockOutTime && (
                    <div className="flex items-center gap-2 text-slate-500 dark:text-white">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      <span className="text-slate-500 dark:text-white">
                        Out: {todayAttendance.attendance.clockOutTime}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-400 dark:text-white font-bold text-sm uppercase tracking-widest">
                  No activity recorded yet
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-4">
            {canClockIn && <ClockInButton type="clock-in" timeRestriction={timeRestriction} />}
            {canClockOut && <ClockInButton type="clock-out" timeRestriction={timeRestriction} />}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="p-4 rounded-xl glass-card dark:glass-card-dark shadow-sm"
          >
            <div className="flex items-center gap-2.5 mb-4">
              <Filter className="w-4 h-4 text-neolab-green" />
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-900 dark:text-white">
                Filter Period
              </h3>
            </div>
            <div className="space-y-3">
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={dateRange?.[0].format('YYYY-MM-DD')}
                  onChange={(e) => setDateRange([dayjs(e.target.value), dateRange?.[1] || dayjs()])}
                  className="w-full pl-10 pr-4 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={dateRange?.[1].format('YYYY-MM-DD')}
                  onChange={(e) => setDateRange([dateRange?.[0] || dayjs(), dayjs(e.target.value)])}
                  className="w-full pl-10 pr-4 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold"
                />
              </div>
            </div>
          </motion.div>

          {summary && (
            <div className="space-y-4">
              {[
                {
                  label: 'Total Days',
                  value: summary.totalDays,
                  icon: Clock,
                  color: 'text-blue-500',
                  bg: 'bg-blue-500/10',
                },
                {
                  label: 'On Time',
                  value: summary.onTimeDays,
                  icon: CheckCircle2,
                  color: 'text-emerald-500',
                  bg: 'bg-emerald-500/10',
                },
                {
                  label: 'Late (Mins)',
                  value: formatMinutes(summary.totalMinutesLate),
                  icon: Clock,
                  color: 'text-amber-500',
                  bg: 'bg-amber-500/10',
                },
                {
                  label: 'Deductions',
                  value: formatNaira(summary.totalDeductions),
                  icon: Wallet,
                  color: 'text-rose-500',
                  bg: 'bg-rose-500/10',
                },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="p-4 rounded-xl glass-card dark:glass-card-dark shadow-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2.5 rounded-lg', stat.bg)}>
                      <stat.icon className={cn('w-4 h-4', stat.color)} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      {stat.label}
                    </span>
                  </div>
                  <span
                    className={cn(
                      'text-base font-black',
                      stat.color.includes('rose')
                        ? 'text-rose-500'
                        : 'text-slate-900 dark:text-white',
                    )}
                  >
                    {stat.value}
                  </span>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden min-h-[400px]"
          >
            <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-neolab-green/10">
                <History className="w-4 h-4 text-neolab-green" />
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                Activity Log
              </h3>
            </div>

            <div className="p-4">
              {attendanceLoading ? (
                <div className="py-20 flex flex-col items-center justify-center space-y-4">
                  <div className="w-10 h-10 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
                  <p className="text-slate-400 font-bold">Syncing records...</p>
                </div>
              ) : attendanceRecords.length === 0 ? (
                <div className="py-20 text-center">
                  <div className="w-20 h-20 bg-neolab-green/5 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Calendar className="w-10 h-10 text-slate-200 dark:text-slate-700" />
                  </div>
                  <p className="text-slate-400 font-bold">No records found for this period</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {attendanceRecords.map((record, i) => (
                    <motion.div
                      key={record._id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + i * 0.05 }}
                    >
                      <AttendanceCard attendance={record} />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Attendance;
